import { Component, EventEmitter, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { InterfaceAppServiceRegister } from '@app/app.interface';
import { AppService } from '@app/app.service';
import { mbHasMatch } from '@fui/fui-helpers';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, ReplaySubject, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FieldConfig } from '../field.interface';

@Component({
    selector: 'app-select-search',
    templateUrl: './select-search.component.html',
    styleUrls: ['../select/select.scss', '../../../../shared/components/button/styles/button.scss'],
})
export class SelectSearchComponent implements OnInit, OnDestroy {
    public appService = inject(AppService);
    public deviceService = inject(DeviceDetectorService);

    /** Флаг, было использовано невалидное значение */
    public isUseInvalidValue: boolean = false;

    /**
     * Тултип для самого поля
     */
    public tooltip = new BehaviorSubject<string | null>(null);

    /** Список с подписками */
    private _subscribeList: Subscription[] = [];

    /** Регулярка для отчистки тегов  */
    regexTag = /( |<([^>]+)>)/gi;
    field: FieldConfig;
    group: FormGroup;
    /**
     * Элементы фильтров
     */
    filterCtrl: FormControl = new FormControl();
    /**
     * Фильтры
     */
    filteredSelect: ReplaySubject<InterfaceAppServiceRegister[]> = new ReplaySubject<InterfaceAppServiceRegister[]>(1);
    private _onDestroy = new Subject<void>();
    data: any = [];
    dataValue: any = {};
    isDataLoad: boolean = false;
    eventAdd: EventEmitter<any>;
    eventUpdateList: EventEmitter<any>;
    @ViewChild('matSelect', { static: false }) matSelect;

    /** Хук жизненного цикла, который срабатывает во время уничтожения компонента */
    ngOnDestroy(): void {
        // Снимаем все подписки, которые существовали в этом компоненте
        this._subscribeList.forEach((item) => {
            item.unsubscribe();
        });
    }

    ngOnInit() {
        if (this?.field?.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
        this.data = [];
        if (this.field.options?.length) {
            this.setOptions(this.field.options);
        }

        this.tooltip.next(this.getCurrentName());
        const filterChange$ = this.filterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
            this.tooltip.next(this.getCurrentName());
            this.filterSelect();
        });
        this._subscribeList.push(filterChange$);

        setTimeout(() => {
            this.matSelect._overlayPanelClass += 'custom-select-panel';
        });
    }

    /** Закрытие списка значений */
    close() {
        this.matSelect.close();
    }

    /**
     * Установка опций селекта
     * @param options
     */
    setOptions(options) {
        this.data = [];
        this.dataValue = {};
        const fieldValue = this.group?.get(this.field.name)?.value;
        options.forEach((value) => {
            if (!this.checkIsDel(value)) {
                this.data.push(value);
                this.dataValue[value.id] = value;
            } else if (this.checkIsDel(value) && fieldValue == value.id) {
                this.data.push(value);
                this.dataValue[value.id] = value;
            }
        });
        if (this.field.addTreeHyphen) {
            this.addTreeHyphen();
        }
        this.filteredSelect.next(this.data.slice());
    }

    /**
     * Генерация данных для дерева
     * @param data данные справочника
     * @param parent_id ид родительской записи
     * @param level уровень ветки в дереве
     */
    generateTreeData(data: any[], parent_id: number | string | null, level: number): any[] {
        const result: any[] = [];
        data.forEach((value) => {
            if (value.parent_id == parent_id) {
                const node: any = Object.assign({}, value);
                node.level = level;
                node.children = this.generateTreeData(data, node.id, level + 1);
                node.is_children = node.children.length !== 0;
                result.push(node);
            }
        });

        return result;
    }

    /**
     * Генерация данных для дерева
     * @param data данные справочника
     */
    generateArrayDataFromTree(data: any[]): any[] {
        const result: any[] = [];
        data.forEach((value) => {
            const node: any = Object.assign({}, value);
            result.push(node);
            if (node.children && node.children.length) {
                const childrenNodes = this.generateArrayDataFromTree(node.children);
                childrenNodes.forEach((childrenNode) => {
                    result.push(childrenNode);
                });
            }
        });
        return result;
    }

    /**
     * Добавление дефисов записям опций как уровней дерева
     */
    addTreeHyphen() {
        this.data = this.generateArrayDataFromTree(this.generateTreeData(this.data, this.field.treeParentId, 1));
    }

    /**
     * Установка запрета изменения поля
     * @param disabled значение флага
     */
    setDisabled(disabled) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }

    /**
     * Фильтр для поиска в селекте
     */
    private filterSelect() {
        if (!this.data) {
            return;
        }
        // Получение значения строки поиска
        const search = this.filterCtrl.value;
        if (!search) {
            this.filteredSelect.next(this.data.slice());
            return;
        }
        // Организация фильтрованного массива
        this.filteredSelect.next(this.data.filter((value) => mbHasMatch(value.name, search)));
    }

    /**
     * Выбор всех значений
     * @param selectAllValue выбранные значения
     */
    toggleSelectAll(selectAllValue: boolean) {
        this.filteredSelect.pipe(take(1), takeUntil(this._onDestroy)).subscribe((val) => {
            if (selectAllValue) {
                const value = [];
                val.forEach((v) => {
                    value.push(v.id);
                });
                this.group.get(this.field.name).setValue(value);
            } else {
                this.group.get(this.field.name).setValue([]);
            }
        });
    }

    /**
     * Проверка проставления всех чекбоксов
     */
    toggleAllCheckboxChecked(): boolean {
        if (!this.field.multiple) {
            return false;
        }
        if (!(typeof this.group?.get(this.field.name)?.value == 'object' && this.group?.get(this.field.name)?.value)) {
            return false;
        }
        return (
            this.data.length &&
            this.data.every((child) => this.group?.get(this.field.name)?.value.indexOf(child.id) !== -1)
        );
    }

    /**
     * Проверка части отмеченных полей
     */
    toggleAllCheckboxIndeterminate(): boolean {
        if (!this.field.multiple) {
            return false;
        }
        if (!(typeof this.group?.get(this.field.name)?.value == 'object' && this.group?.get(this.field.name)?.value)) {
            return false;
        }
        return !this.toggleAllCheckboxChecked() && this.group?.get(this.field.name)?.value.length !== 0;
    }

    /**
     * Обработка имени опции селекта
     * @param option: InterfaceAppServiceRegister
     */
    getOptionName(option: InterfaceAppServiceRegister) {
        let name = option.name;
        if (this.field.optionName) {
            name = option[this.field.optionName];
        }
        if (name && this.field.sanitize) {
            name = name.replace(this.regexTag, ' ');
        }
        return name;
    }

    /**
     * Проверка флага удаление опции в списке.
     * @param option - элемент списка опций
     */
    checkIsDel(option: InterfaceAppServiceRegister): boolean {
        if (option && option.hasOwnProperty('del')) {
            return option.del === true || option.del === '1';
        }

        if (option && option.hasOwnProperty('is_del')) {
            return option.is_del === true || option.is_del === '1';
        }

        return false;
    }

    /** Проверка флага удаление в выбранных опциях */
    checkIsDelFiled(): boolean {
        /** Значение поля */
        const fieldVal = this.group?.get(this.field.name)?.value;

        if (!fieldVal) {
            return false;
        }

        /** Поиск хотя бы одного удаленного значения */
        if (Array.isArray(fieldVal)) {
            for (const i in fieldVal) {
                if (fieldVal[i]) {
                    const val = this.dataValue[fieldVal[i]];
                    if (this.checkIsDel(val)) {
                        this.isUseInvalidValue = true;
                        return true;
                    }
                }
            }
        } else {
            const val = this.dataValue[fieldVal];
            this.isUseInvalidValue = this.checkIsDel(val);
            return this.isUseInvalidValue;
        }

        this.isUseInvalidValue = false;
        return false;
    }

    /**
     * Получить текущее имя
     */
    private getCurrentName(): string | null {
        const value = this.group?.get(this.field.name).value;
        if (!value) {
            return null;
        }

        const options = this.field.options;

        return options.find((item) => +item.id === +value)?.name ?? null;
    }
}
